import { useMemo } from 'react';
import { Square, Triangle } from '@phosphor-icons/react';
import type { ComponentWithValues } from 'common/stack/ComponentWithValuesDocuments';
import type { OnLog } from 'client/state/LogState';
import { Stack } from 'client/components/Stack';
import { createStackComponentFilterFunction, createStackComponentSortFunction } from 'client/components/StatusTree/statusTreeUtils';
import { SectionTitle } from '../SectionTitle';
import type { ComponentsStatusTree } from './componentsStatusUtils';
import { ComponentStatus } from './ComponentStatus';

interface ComponentsStatusProps {
  deploymentID: number | null | undefined;
  deploymentUrl: string;
  componentsStatusTree: ComponentsStatusTree;
  componentsWithValues: ComponentWithValues[];
  onLog: OnLog;
}

export const ComponentsStatus = ({
  deploymentID,
  deploymentUrl,
  componentsStatusTree,
  componentsWithValues,
  onLog,
}: ComponentsStatusProps) => {
  const sortedComponentsStatusTree = useMemo(
    () => componentsStatusTree.components.toSorted(createStackComponentSortFunction({ components: componentsWithValues })),
    [componentsStatusTree.components, componentsWithValues]
  );

  const components = useMemo(
    () =>
      sortedComponentsStatusTree.filter(
        createStackComponentFilterFunction({ components: componentsWithValues, filterByType: 'component' })
      ),
    [sortedComponentsStatusTree, componentsWithValues]
  );

  const addons = useMemo(
    () =>
      sortedComponentsStatusTree.filter(createStackComponentFilterFunction({ components: componentsWithValues, filterByType: 'addon' })),
    [sortedComponentsStatusTree, componentsWithValues]
  );

  return (
    <Stack gap={32}>
      {/* Components */}
      {components.length > 0 && (
        <Stack gap={16}>
          <SectionTitle type="web">
            <Square /> Components
          </SectionTitle>
          <Stack gap={16}>
            {components.map((componentStatusTree) => (
              <ComponentStatus
                key={componentStatusTree.stackComponentID}
                deploymentID={deploymentID}
                deploymentUrl={deploymentUrl}
                componentStatusTree={componentStatusTree}
                component={componentsWithValues.find((component) => component.stackComponentID === componentStatusTree.stackComponentID)}
                onLog={onLog}
              />
            ))}
          </Stack>
        </Stack>
      )}

      {/* Addons */}
      {addons.length > 0 && (
        <Stack gap={16}>
          <SectionTitle type="dev">
            <Triangle /> Add-ons
          </SectionTitle>
          <Stack gap={16}>
            {addons.map((componentStatusTree) => (
              <ComponentStatus
                key={componentStatusTree.stackComponentID}
                deploymentID={deploymentID}
                deploymentUrl={deploymentUrl}
                componentStatusTree={componentStatusTree}
                component={componentsWithValues.find((component) => component.stackComponentID === componentStatusTree.stackComponentID)}
                onLog={onLog}
              />
            ))}
          </Stack>
        </Stack>
      )}
    </Stack>
  );
};
